/**
 * Secure storage utility for handling sensitive data like login credentials
 * Uses browser's built-in encryption where available, with fallbacks
 */

import { logError, logWarning } from './errorLogger';

interface SecureStorageData {
  email: string;
  rememberMe: boolean;
  encrypted?: boolean;
}

class SecureStorage {
  private readonly STORAGE_KEY = 'mirsal_secure_credentials';
  private readonly ENCRYPTION_KEY = 'mirsal_secure_key_2024'; // In production, this should be more secure

  /**
   * Simple encryption using Web Crypto API when available
   */
  private async encrypt(data: string): Promise<string> {
    try {
      // Check if Web Crypto API is available
      if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
        const encoder = new TextEncoder();
        const dataBuffer = encoder.encode(data);
        
        // Create a key from our password
        const keyMaterial = await window.crypto.subtle.importKey(
          'raw',
          encoder.encode(this.ENCRYPTION_KEY),
          { name: 'PBKDF2' },
          false,
          ['deriveBits', 'deriveKey']
        );
        
        const key = await window.crypto.subtle.deriveKey(
          {
            name: 'PBKDF2',
            salt: encoder.encode('mirsal_salt'),
            iterations: 100000,
            hash: 'SHA-256',
          },
          keyMaterial,
          { name: 'AES-GCM', length: 256 },
          false,
          ['encrypt', 'decrypt']
        );
        
        const iv = window.crypto.getRandomValues(new Uint8Array(12));
        const encrypted = await window.crypto.subtle.encrypt(
          { name: 'AES-GCM', iv: iv },
          key,
          dataBuffer
        );
        
        // Combine IV and encrypted data
        const combined = new Uint8Array(iv.length + encrypted.byteLength);
        combined.set(iv);
        combined.set(new Uint8Array(encrypted), iv.length);
        
        return btoa(String.fromCharCode.apply(null, Array.from(combined)));
      }
    } catch (error) {
      logWarning('Web Crypto API not available, using fallback encryption');
    }
    
    // Fallback: Simple base64 encoding (not secure, but better than plain text)
    return btoa(data);
  }

  /**
   * Simple decryption using Web Crypto API when available
   */
  private async decrypt(encryptedData: string): Promise<string> {
    try {
      if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
        const decoder = new TextDecoder();
        const binaryString = atob(encryptedData);
        const combined = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          combined[i] = binaryString.charCodeAt(i);
        }
        
        const iv = combined.slice(0, 12);
        const encrypted = combined.slice(12);
        
        const encoder = new TextEncoder();
        const keyMaterial = await window.crypto.subtle.importKey(
          'raw',
          encoder.encode(this.ENCRYPTION_KEY),
          { name: 'PBKDF2' },
          false,
          ['deriveBits', 'deriveKey']
        );
        
        const key = await window.crypto.subtle.deriveKey(
          {
            name: 'PBKDF2',
            salt: encoder.encode('mirsal_salt'),
            iterations: 100000,
            hash: 'SHA-256',
          },
          keyMaterial,
          { name: 'AES-GCM', length: 256 },
          false,
          ['encrypt', 'decrypt']
        );
        
        const decrypted = await window.crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: iv },
          key,
          encrypted
        );
        
        return decoder.decode(decrypted);
      }
    } catch (error) {
      logWarning('Web Crypto API decryption failed, using fallback');
    }
    
    // Fallback: Simple base64 decoding
    return atob(encryptedData);
  }

  /**
   * Store credentials securely
   */
  async storeCredentials(email: string, rememberMe: boolean): Promise<void> {
    try {
      const data: SecureStorageData = {
        email,
        rememberMe,
        encrypted: true
      };
      
      const encryptedData = await this.encrypt(JSON.stringify(data));
      
      if (rememberMe) {
        localStorage.setItem(this.STORAGE_KEY, encryptedData);
      } else {
        sessionStorage.setItem(this.STORAGE_KEY, encryptedData);
      }
    } catch (error) {
      logError('Failed to store credentials securely', error);
      // Don't store if encryption fails
    }
  }

  /**
   * Retrieve stored credentials
   */
  async getStoredCredentials(): Promise<SecureStorageData | null> {
    try {
      // Check localStorage first (remember me), then sessionStorage
      let encryptedData = localStorage.getItem(this.STORAGE_KEY) || 
                         sessionStorage.getItem(this.STORAGE_KEY);
      
      if (!encryptedData) {
        return null;
      }
      
      const decryptedData = await this.decrypt(encryptedData);
      const data: SecureStorageData = JSON.parse(decryptedData);
      
      return data;
    } catch (error) {
      logError('Failed to retrieve stored credentials', error);
      // Clear corrupted data
      this.clearCredentials();
      return null;
    }
  }

  /**
   * Clear stored credentials
   */
  clearCredentials(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    sessionStorage.removeItem(this.STORAGE_KEY);
  }

  /**
   * Check if credentials are stored
   */
  hasStoredCredentials(): boolean {
    return !!(localStorage.getItem(this.STORAGE_KEY) || sessionStorage.getItem(this.STORAGE_KEY));
  }
}

// Export singleton instance
export const secureStorage = new SecureStorage();

// Security recommendations for production:
// 1. Use a more secure encryption key generation
// 2. Implement proper key rotation
// 3. Consider using hardware security modules (HSM)
// 4. Add integrity checks for stored data
// 5. Implement rate limiting for decryption attempts
// 6. Use Content Security Policy (CSP) headers
// 7. Consider using WebAuthn for passwordless authentication
